// Resolve wiki-image:// and jira-media:// src values on TipTap image nodes
// to attachment-content proxy URLs using the issue's attachment list.
//
// Shared by every component that seeds a RichTextEditor from stored ADF
// (IssueScreenPanel read-mode fields + edit-mode seeding, and
// TransitionScreenModal's rich-text screen fields). Mutates the doc in place.
//
// mediaMap (optional): UUID→{id, filename, ...} from the 303 redirect trick
// endpoint — only relevant for Jira-era UUID media ids.
import api from '@app/services/api';

export default function resolveImageRefs(tipTapDoc, attachments, mediaMap) {
  if (!tipTapDoc) return;
  const attList = attachments || [];
  const map = mediaMap || {};
  const byMediaId = {};
  const byFilename = {};
  for (const att of attList) {
    if (att.mediaApiFileId) byMediaId[att.mediaApiFileId] = att.id;
    if (att.filename) byFilename[att.filename] = att.id;
  }

  (function walk(node) {
    if (!node) return;
    if (node.type === 'image' && node.attrs?.src) {
      if (node.attrs.src.startsWith('jira-media://')) {
        const mediaId = node.attrs.src.replace('jira-media://', '');
        let matchId = null;
        let matchFilename = null;
        // Strategy 0: media map from 303 redirect trick (most reliable for
        // Jira-era UUID media ids)
        if (map[mediaId]) {
          matchId = map[mediaId].id;
          matchFilename = map[mediaId].filename;
        }
        // Strategy 1: the media id IS the Stride attachment id. Files
        // uploaded through Stride's own editor store the numeric attachment
        // id on the ADF media node, so a direct match against the attachment
        // list resolves them — and recovers the real filename for the chip.
        if (!matchId) {
          const direct = attList.find(a => String(a.id) === mediaId);
          if (direct) { matchId = direct.id; matchFilename = direct.filename; }
        }
        // Strategy 2: mediaApiFileId from attachment data
        if (!matchId && byMediaId[mediaId]) matchId = byMediaId[mediaId];
        // Strategy 3: alt filename match
        if (!matchId && node.attrs.alt && node.attrs.alt !== 'Jira attachment') {
          matchId = byFilename[node.attrs.alt];
        }
        // Strategy 4: UUID embedded in attachment filename
        if (!matchId) {
          const found = attList.find(a => a.filename?.includes(mediaId));
          if (found) { matchId = found.id; matchFilename = found.filename; }
        }

        if (matchId) {
          node.attrs.src = api.getAttachmentContentUrl(matchId);
          if (matchFilename && (!node.attrs.alt || node.attrs.alt === 'Jira attachment')) {
            node.attrs.alt = matchFilename;
          }
        }
      } else if (node.attrs.src.startsWith('wiki-image://')) {
        const captured = node.attrs.src.replace('wiki-image://', '');
        let match = null;

        // Strategy 0 — embedded proxy URL. When Jira's wiki-text
        // renderer coerces an ADF media[type=external, url=…] node
        // to the wiki form, it serialises as `!URL!`. wikiMarkupToTipTap
        // captures the URL as the filename, so we end up here with
        // `captured` looking like
        // "https://…/api/attachment-content/<id>". The id alone is
        // enough — once we have it we look the attachment up in the
        // canonical attachment list, exactly the same way the
        // attachment section does, and fix both src and alt. The
        // field's saved content stays unreliable; the attachment
        // list is the source of truth.
        const proxyMatch = captured.match(/\/api\/attachment-content\/(\d+)/);
        if (proxyMatch) {
          const id = proxyMatch[1];
          match = attList.find(a => String(a.id) === id);
        }

        if (!match) {
          // Existing fallbacks — used when the wiki form was
          // `!filename.png!` rather than `!URL!`, i.e. when Jira had
          // a media[type=file] node and coerced via the filename.
          const fid = byFilename[captured];
          if (fid) match = attList.find(a => a.id === fid);
        }
        if (!match) {
          const lower = captured.toLowerCase();
          match = attList.find(a => a.filename?.toLowerCase() === lower);
        }

        if (match) {
          node.attrs.src = api.getAttachmentContentUrl(match.id);
          // Always overwrite alt with the real filename when we found
          // an attachment — the captured value might be the URL,
          // 'Jira attachment', or a stale wiki marker, none of which
          // make a good label for the AttachmentCardView tile.
          node.attrs.alt = match.filename;
        }
      }
    }
    if (node.content) node.content.forEach(walk);
  })(tipTapDoc);
}
